import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { Analytics } from '@/lib/analytics';
import { getLocalDayKey, getLocalDateDaysAgo } from '@/lib/localDateRange';
import { qualityFromSleepStages } from '@/lib/recoveryScore';

export interface SleepLog {
  id: string;
  sleep_date: string;
  hours_slept: number;
  quality_rating: number | null;
  notes: string | null;
  rem_hours?: number | null;
  deep_hours?: number | null;
  awake_minutes?: number | null;
  in_bed_hours?: number | null;
  bed_time?: string | null;
  wake_time?: string | null;
  core_hours?: number | null;
}

/** Health sleep from Apple Health (health_metrics) - includes phase breakdown when available */
export type HealthSleepEntry = {
  date: string;
  hours: number;
  rem_hours?: number;
  deep_hours?: number;
  awake_minutes?: number;
  in_bed_hours?: number;
};

export const useSleepTracking = () => {
  const { user } = useAuth();
  const [sleepLogs, setSleepLogs] = useState<SleepLog[]>([]);
  const [healthSleepData, setHealthSleepData] = useState<HealthSleepEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHealthSleep = async (days = 30) => {
    if (!user?.id) return;
    const startDate = getLocalDateDaysAgo(days);
    const { data } = await supabase
      .from('health_metrics')
      .select('metric_date, sleep_hours, sleep_rem_hours, sleep_deep_hours, sleep_awake_minutes, sleep_in_bed_hours')
      .eq('user_id', user.id)
      .gte('metric_date', getLocalDayKey(startDate))
      .order('metric_date', { ascending: false });
    const entries: HealthSleepEntry[] = ((data ?? []) as {
      metric_date: string;
      sleep_hours: number | null;
      sleep_rem_hours?: number | null;
      sleep_deep_hours?: number | null;
      sleep_awake_minutes?: number | null;
      sleep_in_bed_hours?: number | null;
    }[])
      .filter((r) => r.sleep_hours != null && r.sleep_hours > 0)
      .map((r) => ({
        date: r.metric_date,
        hours: r.sleep_hours!,
        rem_hours: r.sleep_rem_hours ?? undefined,
        deep_hours: r.sleep_deep_hours ?? undefined,
        awake_minutes: r.sleep_awake_minutes ?? undefined,
        in_bed_hours: r.sleep_in_bed_hours ?? undefined,
      }));
    setHealthSleepData(entries);
  };

  const fetchSleepLogs = async (days = 30) => {
    if (!user) return;

    setLoading(true);
    try {
      const startDate = getLocalDateDaysAgo(days);

      const { data, error } = await supabase
        .from('sleep_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('sleep_date', getLocalDayKey(startDate))
        .order('sleep_date', { ascending: false });

      if (error) throw error;
      setSleepLogs(data || []);
    } catch (error) {
      console.error('Error fetching sleep logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const logSleep = async (sleepData: Omit<SleepLog, 'id'>) => {
    if (!user) return;

    try {
      console.log('Logging sleep data:', sleepData);
      
      const { data, error } = await supabase
        .from('sleep_logs')
        .upsert([{
          ...sleepData,
          user_id: user.id
        }], {
          onConflict: 'user_id,sleep_date',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) {
        console.error('Error logging sleep:', error);
        throw error;
      }
      
      console.log('Sleep logged successfully:', data);
      Analytics.sleepLogged(sleepData.hours_slept, sleepData.quality_rating || 5);
      await fetchSleepLogs();
      return data;
    } catch (error) {
      console.error('Error logging sleep:', error);
      throw error;
    }
  };

  const getWeeklyAverage = () => {
    if (sleepLogs.length === 0) return 0;
    
    const last7Days = sleepLogs.slice(0, 7);
    const total = last7Days.reduce((sum, log) => sum + log.hours_slept, 0);
    return Math.round((total / last7Days.length) * 10) / 10;
  };

  const getSleepTrend = () => {
    if (sleepLogs.length < 14) return 'stable';
    
    const recent7Days = sleepLogs.slice(0, 7);
    const previous7Days = sleepLogs.slice(7, 14);
    
    const recentAvg = recent7Days.reduce((sum, log) => sum + log.hours_slept, 0) / recent7Days.length;
    const previousAvg = previous7Days.reduce((sum, log) => sum + log.hours_slept, 0) / previous7Days.length;
    
    if (recentAvg > previousAvg + 0.5) return 'improving';
    if (recentAvg < previousAvg - 0.5) return 'declining';
    return 'stable';
  };

  useEffect(() => {
    if (user) {
      fetchSleepLogs();
      fetchHealthSleep();
    }
  }, [user]);

  useEffect(() => {
    const onSync = () => fetchHealthSleep();
    window.addEventListener('health-sync-complete', onSync);
    return () => window.removeEventListener('health-sync-complete', onSync);
  }, [user?.id]);

  // Sleep for "last night" - the most recent night that just ended.
  //
  // Convention used by HealthKit: a sleep session is attributed to the day on
  // which it STARTED. So a normal "fell asleep yesterday 22:00, woke up today
  // 06:00" night lives on yesterday's metric_date row until the next bedtime
  // crosses midnight. We therefore look at:
  //   1. today  - in case the user logged a nap or HealthKit shifted the date
  //   2. yesterday - the canonical "last night"
  //
  // If neither day has a recorded sleep value, we return null. We DO NOT fall
  // back further into the past - the UI should display "no data" instead of
  // misleadingly showing an old value.
  //
  // This is also intentionally decoupled from steps / calories / etc., which
  // are pulled from health_metrics independently and refresh on their own.
  const mergedTodaySleep = useMemo<{ hours: number | null; quality: number | null }>(() => {
    const today = getLocalDayKey();
    const yesterday = getLocalDayKey(getLocalDateDaysAgo(1));
    const manualToday = sleepLogs.find((log) => log.sleep_date === today);
    const manualYesterday = sleepLogs.find((log) => log.sleep_date === yesterday);
    const healthToday = healthSleepData.find((h) => h.date === today);
    const healthYesterday = healthSleepData.find((h) => h.date === yesterday);

    const positive = (n: number | null | undefined): number | null =>
      n != null && n > 0 ? n : null;

    const hManualT = positive(manualToday?.hours_slept);
    const hHealthT = positive(healthToday?.hours);
    const hManualY = positive(manualYesterday?.hours_slept);
    const hHealthY = positive(healthYesterday?.hours);

    const hours = hManualT ?? hHealthT ?? hManualY ?? hHealthY ?? null;

    /**
     * Recovery uses a 1–5 "sleep quality" alongside duration. Apple Health
     * rows have no manual star rating — previously we left quality null and
     * callers used `?? 0`, which made 7.7h nights score ~67% (30% of the score
     * wiped out). Here we resolve: manual rating → stage-based proxy →
     * neutral 4 when we only know duration.
     */
    const resolveQuality = (h: number): number | null => {
      if (!(h > 0)) return null;

      const fromManual = (log: SleepLog | undefined): number | null => {
        if (!log) return null;
        const q = log.quality_rating;
        if (q != null && q >= 1 && q <= 5) return q;
        const staged = qualityFromSleepStages(h, log.rem_hours, log.deep_hours);
        if (staged != null) return staged;
        return null;
      };

      const fromHealthEntry = (entry: HealthSleepEntry | undefined): number | null => {
        if (!entry) return null;
        return qualityFromSleepStages(h, entry.rem_hours, entry.deep_hours);
      };

      if (hManualT != null && h === hManualT) {
        return fromManual(manualToday) ?? fromHealthEntry(healthToday) ?? 4;
      }
      if (hHealthT != null && h === hHealthT) {
        return fromManual(manualToday) ?? fromHealthEntry(healthToday) ?? 4;
      }
      if (hManualY != null && h === hManualY) {
        return fromManual(manualYesterday) ?? fromHealthEntry(healthYesterday) ?? 4;
      }
      if (hHealthY != null && h === hHealthY) {
        return fromManual(manualYesterday) ?? fromHealthEntry(healthYesterday) ?? 4;
      }
      return null;
    };

    const quality = hours != null ? resolveQuality(hours) : null;

    return { hours, quality };
  }, [sleepLogs, healthSleepData]);

  return {
    sleepLogs,
    healthSleepData,
    mergedTodaySleep,
    loading,
    logSleep,
    getWeeklyAverage,
    getSleepTrend,
    refetch: async () => {
      await fetchSleepLogs();
      await fetchHealthSleep();
    },
  };
};